// Offline helpers voor de Vandaag-app.
// - GET response cache voor lees-modus (planning, projecten, voertuigen)
// - Queue voor start/stop timer acties → replay bij online
// LocalStorage volstaat: dagplanning is klein (< 200KB) en actions zijn tekst.

const CACHE_PREFIX = "pb_cache:";
const QUEUE_KEY = "pb_offline_queue";
const LOCAL_TIMER_KEY = "pb_local_timer";

// Alleen deze GET-paden cachen we voor offline lees-modus.
const CACHEABLE_GETS = [
  "/vandaag",
  "/projects",
  "/vehicles",
  "/users",
  "/time-entries/active",
  "/time-entries",
  "/assignments",
];

export function isCacheableGet(url) {
  if (!url) return false;
  const path = url.split("?")[0];
  return CACHEABLE_GETS.some((p) => path.endsWith(p) || path.includes(`${p}?`) || path === p);
}

function cacheKey(method, url) {
  return `${CACHE_PREFIX}${method.toUpperCase()}:${url}`;
}

export function saveCache(method, url, data) {
  try {
    const raw = JSON.stringify({ data, savedAt: Date.now() });
    if (raw.length > 200000) return;
    localStorage.setItem(cacheKey(method, url), raw);
  } catch (_) { /* quota */ }
}

export function readCache(method, url) {
  try {
    const raw = localStorage.getItem(cacheKey(method, url));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

// ---------- Queue ----------
export function readQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
  catch { return []; }
}

function writeQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }
  catch (_) { /* private mode / quota — offline queue silently disabled */ }
}

export function enqueue(action) {
  const q = readQueue();
  q.push({ ...action, id: action.id || `${Date.now()}-${Math.random().toString(36).slice(2,8)}`, queuedAt: new Date().toISOString() });
  writeQueue(q);
  return q.length;
}

export function queueLength() { return readQueue().length; }

// Replay queue in strict order — stops on 5xx/network err, keeps action for next try.
export async function flushQueue(apiInstance) {
  let q = readQueue();
  if (q.length === 0) return { ok: true, flushed: 0, failed: 0 };
  let flushed = 0; let failed = 0;
  while (q.length > 0) {
    const action = q[0];
    try {
      await apiInstance.request({
        method: action.method || "POST",
        url: action.url,
        data: action.body || {},
      });
      flushed += 1;
      q.shift();
      writeQueue(q);
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 425 && status !== 429) {
        // permanent failure → drop the action so we don't loop forever
        failed += 1;
        q.shift();
        writeQueue(q);
      } else {
        // network or 5xx → keep and stop, we'll try again next time we come online
        return { ok: false, flushed, failed, remaining: q.length };
      }
    }
  }
  return { ok: true, flushed, failed, remaining: 0 };
}

// ---------- Local timer state (for optimistic UI when offline) ----------
export function saveLocalTimer(entry) {
  try {
    if (entry === null) localStorage.removeItem(LOCAL_TIMER_KEY);
    else localStorage.setItem(LOCAL_TIMER_KEY, JSON.stringify(entry));
  } catch (_) { /* quota */ }
}

export function readLocalTimer() {
  try { const raw = localStorage.getItem(LOCAL_TIMER_KEY); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
}

export function clientId() {
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}
